// HowItWorks.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

export function HowItWorks() {
  const steps = [
    { n: '01', title: 'Choose a navigation activity', body: 'Open the Activities page and select one of the three original navigation activities. No account or registration is required. No payment information is ever requested.' },
    { n: '02', title: 'Session points appear automatically', body: 'When an activity loads, a session point counter appears. These points begin at zero and accumulate as you engage with the activity. They are internal feedback only.' },
    { n: '03', title: 'Interact with the activity', body: 'Each activity has a distinct interaction model — stopping a compass needle, tracing a hex-grid route, or marking an altitude reading. Follow the on-screen instructions.' },
    { n: '04', title: 'Points reflect your accuracy', body: 'The more precisely you complete a navigation task, the more session points are recorded. Higher rounds introduce more challenging parameters.' },
    { n: '05', title: 'Session ends when you leave', body: 'Closing the browser tab, navigating away, or refreshing the page ends your session. All session points clear. There is no account to save them to, and no reason to save them — they have no value.' },
  ];

  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container" style={{ maxWidth: '800px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>Platform Guide</div>
          <h1 style={{ color: '#fff', marginBottom: '16px' }}>How It Works</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '16px', fontSize: '1.2rem' }}>From first click to session end</h2>
          <div className="divider" />
          <p style={{ maxWidth: '520px', marginTop: '16px', marginBottom: '48px' }}>
            Social North Hub is a browser-based entertainment platform. Here is a plain-language explanation of how everything works.
          </p>
        </motion.div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {steps.map(({ n, title, body }, i) => (
            <motion.div key={n}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              style={{ display: 'flex', gap: '24px', paddingBottom: '36px', position: 'relative' }}
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div style={{ position: 'absolute', left: '19px', top: '44px', bottom: '0', width: '1px', background: 'linear-gradient(transparent, rgba(201,168,76,0.15), transparent)' }} />
              )}
              <div style={{
                width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%',
                border: '1.5px solid rgba(201,168,76,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '0.8rem', color: '#c9a84c',
                background: 'rgba(201,168,76,0.06)',
              }}>
                {n}
              </div>
              <div style={{ paddingTop: '8px' }}>
                <h3 style={{ color: '#fff', marginBottom: '8px' }}>{title}</h3>
                <p style={{ fontSize: '0.92rem' }}>{body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Session points clarification */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          style={{
            background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: '16px', padding: '28px', marginTop: '20px',
          }}
        >
          <h3 style={{ color: '#c9a84c', marginBottom: '14px' }}>What Session Points Are Not</h3>
          <p style={{ marginBottom: '14px', fontSize: '0.9rem' }}>
            Session points are internal entertainment counters. They do not represent money, prizes, or any real-world benefit. They cannot be:
          </p>
          {['Purchased with real money', 'Redeemed for prizes or goods', 'Transferred to another person', 'Exchanged for currency', 'Saved between sessions', 'Used outside this platform'].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid rgba(201,168,76,0.08)', fontSize: '0.88rem', color: 'rgba(168,207,224,0.6)' }}>
              <span style={{ color: 'rgba(201,168,76,0.5)' }}>✗</span> {item}
            </div>
          ))}
        </motion.div>

        <div style={{ marginTop: '36px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <Link to="/activities" className="btn btn-primary">Open Activities</Link>
          <Link to="/mindful-play" className="btn btn-outline">Mindful Play Guide</Link>
        </div>
      </div>
    </div>
  );
}

// AboutHub.jsx
export function AboutHub() {
  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container" style={{ maxWidth: '760px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>The Hub</div>
          <h1 style={{ color: '#fff', marginBottom: '16px' }}>About Social North Hub</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '16px', fontSize: '1.2rem' }}>What this platform is and is not</h2>
          <div className="divider" />
        </motion.div>

        {[
          {
            heading: 'What Social North Hub is',
            text: 'Social North Hub is a browser-based entertainment platform for Canadian adults aged 18 and over. It offers three original navigation-themed activities: Compass Read, Route Builder, and Altitude Gauge. All activities use session points only. Session points have no monetary value and cannot be purchased, transferred, or redeemed.',
          },
          {
            heading: 'Why navigation',
            text: 'Navigation was chosen as the central theme because it rewards patience and spatial reasoning rather than speed and reflex alone. A compass needle, a hex terrain, and an altitude scale are all instruments that require observation — not luck. We wanted activities that feel calm and deliberate.',
          },
          {
            heading: 'What this platform is not',
            text: 'Social North Hub is not a real-money gaming platform, a lottery, a sweepstakes operator, or any kind of gambling service. No deposits are accepted. No withdrawals are processed. No prizes of monetary value are awarded. The word "casino" does not describe this platform and is not used anywhere on it.',
          },
          {
            heading: 'Who operates it',
            text: 'Social North Hub is operated by Social North Hub, a Canadian entertainment publisher. For support, contact support@socialnorthhub.com. For legal enquiries, contact legal@socialnorthhub.com.',
          },
          {
            heading: 'Original activities',
            text: 'All three activities were built specifically for this platform. The compass mechanic, the hex-grid pathfinding system, and the altitude gauge are not shared with, copied from, or embedded from any other platform. There are no third-party game engines on this site.',
          },
        ].map(({ heading, text }, i) => (
          <motion.div key={heading}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            style={{ marginTop: '36px' }}
          >
            <h3 style={{ color: '#fff', marginBottom: '10px' }}>{heading}</h3>
            <p style={{ fontSize: '0.95rem' }}>{text}</p>
          </motion.div>
        ))}

        <div style={{ marginTop: '48px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <Link to="/activities" className="btn btn-primary">View Activities</Link>
          <Link to="/reach-us" className="btn btn-outline">Contact Us</Link>
        </div>
      </div>
    </div>
  );
}

// MindfulPlay.jsx
export function MindfulPlay() {
  const resources = [
    { name: 'ConnexOntario', desc: 'Free, confidential support for mental health and substance use in Ontario.', contact: '1-866-531-2600 · 24/7', url: 'https://www.connexontario.ca' },
    { name: 'Gambling Support BC', desc: 'Problem gambling support and information for British Columbia residents.', contact: '1-888-795-6111', url: 'https://www.bcresponsiblegambling.ca' },
    { name: 'Responsible Gambling Council', desc: 'National Canadian resource for research, education, and support around problem gambling.', contact: 'responsiblegambling.org', url: 'https://www.responsiblegambling.org' },
  ];

  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container" style={{ maxWidth: '760px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>Wellbeing</div>
          <h1 style={{ color: '#fff', marginBottom: '16px' }}>Mindful Play</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '16px', fontSize: '1.2rem' }}>Keep play balanced and intentional</h2>
          <div className="divider" />
          <p style={{ marginTop: '18px', marginBottom: '36px', maxWidth: '540px' }}>
            Browser-based activities are most enjoyable when approached with intention. This page offers simple guidance for keeping your time here light and balanced.
          </p>
        </motion.div>

        {/* Guidance */}
        <h3 style={{ color: '#fff', marginBottom: '14px' }}>Play habits that help</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '48px' }}>
          {[
            { icon: '⏱', title: 'Set a time limit before you start', desc: 'Decide in advance how long you want to spend. When the time arrives, honour it.' },
            { icon: '🧘', title: 'Take a break every 20 minutes', desc: 'Step away from the screen, move around, and return only if you still want to.' },
            { icon: '🎯', title: 'Play for the activity, not the points', desc: 'Session points have no value. The enjoyment is in the navigation task itself.' },
            { icon: '🚦', title: 'Stop if it stops feeling light', desc: 'If any activity starts to feel like an obligation or source of stress, close the browser.' },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="card" style={{ padding: '20px' }}>
              <div style={{ fontSize: '1.8rem', marginBottom: '10px' }}>{icon}</div>
              <h4 style={{ color: '#fff', marginBottom: '6px', fontSize: '0.95rem' }}>{title}</h4>
              <p style={{ fontSize: '0.85rem' }}>{desc}</p>
            </div>
          ))}
        </div>

        {/* Support */}
        <div style={{ background: 'rgba(168,207,224,0.04)', border: '1px solid rgba(168,207,224,0.1)', borderRadius: '16px', padding: '28px', marginBottom: '24px' }}>
          <h3 style={{ color: '#fff', marginBottom: '6px' }}>If you need support</h3>
          <p style={{ fontSize: '0.88rem', marginBottom: '24px' }}>
            Social North Hub is not a gambling platform, but if you or someone you know is experiencing concerns related to gaming habits, these Canadian resources are available at no cost:
          </p>
          {resources.map(({ name, desc, contact, url }) => (
            <div key={name} style={{ padding: '16px 0', borderBottom: '1px solid rgba(168,207,224,0.08)' }}>
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '1rem', color: '#c9a84c', textDecoration: 'none', display: 'block', marginBottom: '4px' }}>{name}</a>
              <div style={{ fontSize: '0.85rem', color: 'rgba(168,207,224,0.6)', marginBottom: '4px' }}>{desc}</div>
              <div style={{ fontSize: '0.8rem', color: 'rgba(168,207,224,0.4)' }}>{contact}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ReachUs.jsx
export function ReachUs() {
  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container" style={{ maxWidth: '700px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>Get in touch</div>
          <h1 style={{ color: '#fff', marginBottom: '16px' }}>Reach Us</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '16px', fontSize: '1.2rem' }}>Support and legal contacts</h2>
          <div className="divider" />
          <p style={{ marginTop: '18px', marginBottom: '40px' }}>
            Have a question about the platform, an activity, or a policy matter? Use the contact details below.
          </p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '40px' }}>
          {[
            { label: 'General Support', email: 'support@socialnorthhub.com', desc: 'Questions about activities, session points, or general platform use.' },
            { label: 'Legal & Privacy', email: 'legal@socialnorthhub.com', desc: 'Privacy requests, Terms of Use questions, or legal correspondence.' },
          ].map(({ label, email, desc }) => (
            <div key={label} className="card">
              <div className="label label-amber" style={{ marginBottom: '12px' }}>{label}</div>
              <a href={`mailto:${email}`} style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.9rem', fontWeight: 600, color: '#c9a84c', display: 'block', marginBottom: '8px' }}>{email}</a>
              <p style={{ fontSize: '0.85rem' }}>{desc}</p>
            </div>
          ))}
        </div>

        <div style={{ background: 'rgba(168,207,224,0.04)', border: '1px solid rgba(168,207,224,0.1)', borderRadius: '12px', padding: '20px 24px', fontSize: '0.85rem', color: 'rgba(168,207,224,0.5)', lineHeight: 1.75 }}>
          <strong style={{ color: 'rgba(168,207,224,0.75)', display: 'block', marginBottom: '6px' }}>Publisher</strong>
          Social North Hub<br />
          support@socialnorthhub.com<br />
          Canada · Entertainment use only
        </div>
      </div>
    </div>
  );
}

// HubTerms.jsx
export function HubTerms() {
  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container" style={{ maxWidth: '760px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>Legal</div>
          <h1 style={{ color: '#fff', marginBottom: '8px' }}>Terms of Use</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '14px', fontSize: '1.2rem' }}>Rules for using Social North Hub</h2>
          <p style={{ fontSize: '0.8rem', color: 'rgba(168,207,224,0.4)', marginBottom: '16px' }}>Effective: 2025. Publisher: Social North Hub.</p>
          <div className="divider" />
        </motion.div>

        {[
          { title: 'What this platform is', text: 'Social North Hub is a free browser-based entertainment platform for adults aged 18 and older in Canada. It provides three original navigation activities — Compass Read, Route Builder, and Altitude Gauge. This is not a real-money platform, lottery, sweepstakes, or any form of gambling service.' },
          { title: 'Age requirement', text: 'You must be 18 years of age or older to use this platform. By accessing any activity on Social North Hub, you confirm that you meet this requirement. The platform is not intended for persons under 18.' },
          { title: 'Session points', text: 'All activities use session points. Session points are internal entertainment counters with no monetary value. They cannot be purchased with real money, redeemed for goods or prizes, transferred to other users, or exchanged for anything outside this platform. Session points disappear when your browser session ends.' },
          { title: 'No financial transactions', text: 'Social North Hub does not accept deposits, process withdrawals, or award monetary prizes of any kind. No payment information is collected. No financial transactions of any nature take place on this platform.' },
          { title: 'Acceptable use', text: 'This platform is for personal, non-commercial entertainment only. You may not use automated tools, scripts, or bots to interact with activities. You may not attempt to exploit, reverse-engineer, or modify the platform in any way.' },
          { title: 'Intellectual property', text: 'All content on Social North Hub — including the navigation activities, visual design, and written material — is original to this platform. You may not reproduce, copy, or distribute any part of this platform without written permission.' },
          { title: 'Availability', text: 'Social North Hub makes no guarantee of uninterrupted access. The platform may be unavailable for maintenance or updated without notice.' },
          { title: 'Contact', text: 'For legal enquiries: legal@socialnorthhub.com · For general support: support@socialnorthhub.com' },
        ].map(({ title, text }) => (
          <div key={title} style={{ marginTop: '32px' }}>
            <h3 style={{ color: '#fff', marginBottom: '10px', fontSize: '1.1rem' }}>{title}</h3>
            <p style={{ fontSize: '0.92rem', lineHeight: 1.8 }}>{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Privacy.jsx
export function Privacy() {
  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container" style={{ maxWidth: '760px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>Legal</div>
          <h1 style={{ color: '#fff', marginBottom: '8px' }}>Privacy Policy</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '14px', fontSize: '1.2rem' }}>How we handle data</h2>
          <p style={{ fontSize: '0.8rem', color: 'rgba(168,207,224,0.4)', marginBottom: '16px' }}>Effective: 2025. Social North Hub collects minimal data.</p>
          <div className="divider" />
        </motion.div>

        {[
          { title: 'Our approach to privacy', text: 'Social North Hub is designed to work without collecting personal information. You do not need to create an account to use any activity on this platform. No payment data is collected because no payments occur here.' },
          { title: 'What we collect', text: 'We may collect standard server log data such as IP address, browser type, and pages visited. This data is used only for platform security and performance monitoring. It is not used to identify individuals and is not shared with third parties for advertising purposes.' },
          { title: 'What we do not collect', text: 'We do not collect: your name, email address, date of birth, payment information, government ID, or any other personal identifier. We do not require registration and therefore maintain no user accounts or profile data.' },
          { title: 'Session points and gameplay data', text: 'Session points exist only in your browser during a session. They are not stored on our servers and disappear when you leave. No gameplay history is retained.' },
          { title: 'Cookies', text: 'This platform may use essential session cookies to maintain basic functionality during your visit. We do not use advertising cookies or cross-site tracking. Please see our Cookie Notice for details.' },
          { title: 'Canadian privacy law', text: 'Social North Hub operates in accordance with the Personal Information Protection and Electronic Documents Act (PIPEDA) and applicable provincial privacy legislation.' },
          { title: 'Contact', text: 'For privacy-related enquiries: legal@socialnorthhub.com' },
        ].map(({ title, text }) => (
          <div key={title} style={{ marginTop: '32px' }}>
            <h3 style={{ color: '#fff', marginBottom: '10px', fontSize: '1.1rem' }}>{title}</h3>
            <p style={{ fontSize: '0.92rem', lineHeight: 1.8 }}>{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// CookieNotice.jsx
export function CookieNotice() {
  return (
    <div style={{ minHeight: '100vh', padding: '60px 0 100px' }}>
      <div className="container" style={{ maxWidth: '720px' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="label label-sky" style={{ marginBottom: '14px' }}>Legal</div>
          <h1 style={{ color: '#fff', marginBottom: '8px' }}>Cookie Notice</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', marginBottom: '14px', fontSize: '1.2rem' }}>Essential cookies explained</h2>
          <div className="divider" />
        </motion.div>

        {[
          { title: 'What cookies are', text: 'Cookies are small text files stored in your browser when you visit a website. Social North Hub uses them only for essential platform functionality.' },
          { title: 'Cookies we use', text: 'We use session cookies that exist only during your visit and are deleted when you close your browser. These help the platform remember your position within an activity during a single session. We do not use persistent tracking cookies, advertising cookies, or third-party analytics cookies.' },
          { title: 'Cookies we do not use', text: 'We do not use: advertising or retargeting cookies, social media tracking pixels, cross-site analytics identifiers, or any cookies that track you beyond this platform.' },
          { title: 'Managing cookies', text: 'You can disable cookies in your browser settings. Disabling cookies may affect the functionality of in-session activity counters but will not prevent you from accessing the platform.' },
          { title: 'Contact', text: 'For cookie-related questions: legal@socialnorthhub.com' },
        ].map(({ title, text }) => (
          <div key={title} style={{ marginTop: '32px' }}>
            <h3 style={{ color: '#fff', marginBottom: '10px', fontSize: '1.1rem' }}>{title}</h3>
            <p style={{ fontSize: '0.92rem', lineHeight: 1.8 }}>{text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// NotFound.jsx
export function NotFound() {
  return (
    <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '60px 24px' }}>
      <div>
        <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '6rem', fontWeight: 800, color: 'rgba(201,168,76,0.2)', lineHeight: 1, marginBottom: '16px' }}>404</div>
        <h1 style={{ color: '#fff', marginBottom: '8px' }}>Page not found</h1>
        <h2 style={{ color: '#fff', marginBottom: '12px' }}>Off the chart</h2>
        <p style={{ color: 'rgba(168,207,224,0.5)', marginBottom: '28px' }}>This coordinate doesn't exist on our map.</p>
        <Link to="/" className="btn btn-primary">Return to hub</Link>
      </div>
    </div>
  );
}

export default HowItWorks;
